// A2F-764: simple-config table-style editor.
//
// Same shape as v1's TableStylesEditor (presets + activeIdx + onChange
// + onActiveChange) but the editor body is consolidated to the 6
// knobs that move the needle for regulatory tables: header bg /
// header text / header style / alt-row shading / borders / preset
// name. The other 7 fields per preset (header font, header size,
// body font/size/color, default cell bg, border color) stay at v1
// schema defaults — v1 users with custom values keep them on save,
// the v2 UI just doesn't expose editors.
//
// Preset switching, add, reorder, delete — same affordances v1's
// PresetCard exposes, just laid out as a compact tab strip with the
// active preset's row carrying the inline ↑↓× buttons.

import type { TableStylePreset } from '../../types'
import { ColorSwatch, TABLE_COLOR_PALETTE } from './ColorSwatch'
import { Segmented } from './Segmented'
import { Toggle } from '../onboard/v2/Toggle'

// ── Knob value maps ──────────────────────────────────────────────────

const HEADER_TEXT_PALETTE = ['#FFFFFF', '#221610', '#475569', '#000000'] as const

const HEADER_STYLE_OPTIONS = [
  { value: 'plain' as const, label: 'Plain' },
  { value: 'bold' as const, label: 'Bold' },
] as const

// Border weights map to width in pt. None=0 hides borders entirely.
// Standard=0.5pt is the v1 Default seed; Light=0.25pt halves it for
// looser tables; Bold=1pt is the heavier "submission table" look.
const BORDER_OPTIONS = [
  { value: 0 as const, label: 'None' },
  { value: 0.25 as const, label: 'Light' },
  { value: 0.5 as const, label: 'Standard' },
  { value: 1 as const, label: 'Bold' },
] as const

// Tinted alt-row color when shading is on. Light enough to read against
// while still visible — matches the slate-50 used elsewhere in v2.
const ALT_ROW_TINT = '#F1F5F9'

function newPresetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'p-' + Math.random().toString(36).slice(2, 14)
}

const NEW_PRESET_DEFAULTS: Omit<TableStylePreset, 'id' | 'name'> = {
  headerBg: '#221610',
  headerFont: 'Calibri',
  headerFontSize: 11,
  headerTextColor: '#FFFFFF',
  headerBold: true,
  bodyFont: 'Calibri',
  bodyFontSize: 10,
  bodyTextColor: '#221610',
  defaultCellBg: '#FFFFFF',
  altRowBg: '#FFFFFF',
  borderColor: '#999999',
  borderWidth: 0.5,
}

// ── Component ────────────────────────────────────────────────────────

interface SectionTableStylesProps {
  presets: TableStylePreset[]
  activeIdx: number
  onChange: (next: TableStylePreset[]) => void
  onActiveChange: (idx: number) => void
}

export function SectionTableStyles({
  presets,
  activeIdx,
  onChange,
  onActiveChange,
}: SectionTableStylesProps) {
  const active = presets[Math.min(activeIdx, Math.max(0, presets.length - 1))]
  if (!active) {
    // Defensive — presets is never empty in practice (server seeds
    // Default), but the slice guard above can return undefined for an
    // empty array.
    return null
  }

  function update(patch: Partial<TableStylePreset>): void {
    const next = presets.slice()
    next[activeIdx] = { ...active, ...patch }
    onChange(next)
  }

  function add(): void {
    const next = presets.slice()
    next.push({ id: newPresetId(), name: 'New preset', ...NEW_PRESET_DEFAULTS })
    onChange(next)
    onActiveChange(next.length - 1)
  }

  function move(dir: -1 | 1): void {
    const target = activeIdx + dir
    if (target < 0 || target >= presets.length) return
    const next = presets.slice()
    const [moved] = next.splice(activeIdx, 1)
    next.splice(target, 0, moved)
    onChange(next)
    onActiveChange(target)
  }

  function remove(): void {
    if (presets.length <= 1) return
    const next = presets.slice()
    next.splice(activeIdx, 1)
    onChange(next)
    onActiveChange(Math.max(0, activeIdx - 1))
  }

  // Derived: alt-row toggle reflects whether altRowBg differs from
  // defaultCellBg (case-insensitive hex compare).
  const altRowOn =
    String(active.altRowBg ?? '').toLowerCase() !==
    String(active.defaultCellBg ?? '#FFFFFF').toLowerCase()

  // Derived: header style — bold flag → segmented.
  const headerStyle: 'plain' | 'bold' = active.headerBold ? 'bold' : 'plain'

  // Derived: border weight — snap raw width to nearest segmented
  // value so a v1 profile with a custom width still picks a defined
  // segment (closest of {0, 0.25, 0.5, 1}). When user picks a
  // segment we write the snapped value verbatim.
  const borderWeight = snapBorderWidth(active.borderWidth ?? 0.5)

  return (
    <div className="space-y-6">
      {/* Preset tabs row */}
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((p, i) => {
          const selected = i === activeIdx
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onActiveChange(i)}
              aria-pressed={selected}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[12px] transition-colors border ${
                selected
                  ? 'bg-brand-orange/5 border-brand-orange text-brand-orange'
                  : 'bg-white border-slate-200 text-brand-charcoal hover:border-slate-300'
              }`}
            >
              <span
                aria-hidden
                className="inline-block h-3.5 w-3.5 rounded-sm border border-slate-300"
                style={{ background: p.headerBg ?? '#221610' }}
              />
              <span className="truncate max-w-[140px]">{p.name}</span>
            </button>
          )
        })}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-1.5 text-[12px] text-slate-600 hover:border-brand-orange hover:text-brand-orange"
        >
          + Add preset
        </button>
      </div>

      {/* Active preset name + reorder/delete */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
          Preset name
          <span className="ml-2 normal-case tracking-normal font-normal text-slate-400">
            shown as a row in the Apply Table Style task pane
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={active.name}
            onChange={(e) => update({ name: e.target.value })}
            maxLength={80}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
          />
          <button
            type="button"
            onClick={() => move(-1)}
            disabled={activeIdx === 0}
            aria-label="Move preset up"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-500 hover:text-brand-charcoal disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => move(1)}
            disabled={activeIdx === presets.length - 1}
            aria-label="Move preset down"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-500 hover:text-brand-charcoal disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={presets.length <= 1}
            aria-label="Delete preset"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-500 hover:border-red-300 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ×
          </button>
        </div>
      </div>

      {/* Header background — full-width row so the 8-swatch palette
          fits one line at the column widths we render at. Pairing it
          with HEADER TEXT in a 2-col grid forced the palette into 2
          rows because each cell only got ~half the column. */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
          Header background
        </div>
        <ColorSwatch
          value={String(active.headerBg ?? '#221610')}
          palette={TABLE_COLOR_PALETTE}
          ariaLabelPrefix="Header background"
          onChange={(hex) => update({ headerBg: hex })}
        />
      </div>

      {/* Header text + style */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
            Header text
          </div>
          <ColorSwatch
            value={String(active.headerTextColor ?? '#FFFFFF')}
            palette={HEADER_TEXT_PALETTE}
            ariaLabelPrefix="Header text"
            onChange={(hex) => update({ headerTextColor: hex })}
          />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
            Header style
          </div>
          <Segmented
            value={headerStyle}
            options={HEADER_STYLE_OPTIONS}
            ariaLabel="Header style"
            onChange={(v) => update({ headerBold: v === 'bold' })}
          />
        </div>
      </div>

      {/* Alt-row shading + borders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
            Alt-row shading
          </div>
          <div className="flex items-center gap-2">
            <Toggle
              on={altRowOn}
              accent="orange"
              ariaLabel="Alternating row shading"
              onClick={() =>
                update({
                  altRowBg: altRowOn
                    ? (active.defaultCellBg ?? '#FFFFFF')
                    : ALT_ROW_TINT,
                })
              }
            />
            <span className="text-[12px] text-slate-600">
              {altRowOn ? 'On — every other row tinted' : 'Off'}
            </span>
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
            Borders
          </div>
          <Segmented
            value={borderWeight}
            options={BORDER_OPTIONS}
            ariaLabel="Border weight"
            onChange={(v) => update({ borderWidth: v })}
          />
        </div>
      </div>
    </div>
  )
}

function snapBorderWidth(w: number): 0 | 0.25 | 0.5 | 1 {
  // Closest of the segmented values. Keeps a v1 profile with a custom
  // 0.75pt width visible as 'Bold' (1pt) until the user re-picks.
  const opts = [0, 0.25, 0.5, 1] as const
  let best: 0 | 0.25 | 0.5 | 1 = 0.5
  let bestDiff = Infinity
  for (const o of opts) {
    const d = Math.abs(w - o)
    if (d < bestDiff) {
      bestDiff = d
      best = o
    }
  }
  return best
}
